import { useState } from "react";

const ROLE_OPTIONS = [
  { value: "ignore", label: "Ignore" },
  { value: "fullName", label: "Name (full)" },
  { value: "firstName", label: "Name (first)" },
  { value: "lastName", label: "Name (last)" },
  { value: "email", label: "Email" },
  { value: "affiliation", label: "Affiliation" },
  { value: "judge", label: "Judging (checkbox)" },
  { value: "disciplineCheckbox", label: "Discipline (checkbox)" },
  { value: "disciplineList", label: "Discipline (list per row)" },
];

const SINGULAR_ROLES = ["firstName", "lastName", "fullName", "email", "affiliation", "judge"];

const sampleValues = (rows, header) => {
  const seen = [];
  for (const row of rows) {
    const v = (row[header] || "").toString().trim();
    if (v && !seen.includes(v)) seen.push(v);
    if (seen.length === 3) break;
  }
  return seen;
};

// Shown after a roster file is parsed, before RosterImportPreview: lets the admin
// confirm or override the auto-detected role of each column, since no fixed set of
// header synonyms can guarantee a correct guess for every organizer's spreadsheet.
export default function RosterColumnMapping({ parsed, defaultMapping, onConfirm, onCancel }) {
  const [mapping, setMapping] = useState(defaultMapping);

  const setRole = (header, role) => {
    setMapping((prev) => prev.map((m) => (m.header === header ? { ...m, role } : m)));
  };

  const roleCounts = mapping.reduce((acc, m) => {
    acc[m.role] = (acc[m.role] || 0) + 1;
    return acc;
  }, {});
  const duplicateSingularRoles = SINGULAR_ROLES.filter((r) => (roleCounts[r] || 0) > 1);
  const hasName = (roleCounts.fullName || 0) > 0 || ((roleCounts.firstName || 0) > 0 && (roleCounts.lastName || 0) > 0);
  const canConfirm = hasName && duplicateSingularRoles.length === 0;

  return (
    <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-3">
      <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-1">MATCH COLUMNS</div>
      <div className="text-xs text-gray-600 mb-3">
        We guessed what each column means below — check it and adjust anything that's wrong.
      </div>

      <div className="flex flex-col gap-2 mb-3">
        {mapping.map((m) => (
          <div key={m.header} className="flex items-center gap-2 p-2.5 bg-dark-800 border border-dark-500 rounded-lg">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{m.header}</div>
              <div className="text-xs text-gray-600 truncate">
                e.g. {sampleValues(parsed.rows, m.header).join(" | ") || "(no sample values)"}
              </div>
            </div>
            <select aria-label={`Role for ${m.header} column`} value={m.role} onChange={(e) => setRole(m.header, e.target.value)}
              className="p-2 bg-dark-700 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400 shrink-0">
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {!hasName && (
        <p className="text-xs text-yellow-500 mb-2">
          Pick a Name column (either "Name (full)", or both "Name (first)" and "Name (last)") to continue.
        </p>
      )}
      {duplicateSingularRoles.length > 0 && (
        <p className="text-xs text-yellow-500 mb-2">
          Only one column can be marked {duplicateSingularRoles.map((r) => ROLE_OPTIONS.find((o) => o.value === r).label).join(", ")}.
        </p>
      )}

      <div className="flex gap-2 mt-2">
        <button type="button" onClick={onCancel}
          className="flex-1 p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-gray-500 text-sm font-bold cursor-pointer">
          Cancel
        </button>
        <button type="button" onClick={() => onConfirm(mapping)} disabled={!canConfirm}
          className="flex-1 p-2.5 bg-green-action-light text-white rounded-lg text-sm font-display font-bold tracking-wide disabled:opacity-40 border-none cursor-pointer">
          Continue
        </button>
      </div>
    </div>
  );
}
